"use client"

import { ChangeTrend } from "@/app/collections/leaderboard/view/change-trend"
import { useCollectionStatitstic } from "@/app/collections/statistic/use-floor"
import { Image } from "@/app/components/image"
import { PriceInUsd } from "@/app/price"
import { joinAddress } from "@anaconda/core/blockchain/address"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import { cn } from "@rarible_int/anaconda_ui_components"

interface ListItem {
  id: string
  title: string
  description?: string
  image?: string
  collectionId?: string
  blockchain?: string
  ctaText?: string
  ctaUrl?: string
  metadata?: Record<string, unknown>
  link?: string // Deprecated in favor of ctaUrl, but kept for backward compatibility
}

interface ListSectionProps {
  title: string
  description?: string
  layout: "media" | "merch" | "collectors" | "collections" | "characters"
  columns?: 2 | 3 | 4 | 6
  items: ListItem[]
  ctaText?: string
  ctaLink?: string
  spacing?: "default" | "large" // New prop for spacing variants
}

interface ListSectionComponentProps {
  props: ListSectionProps
  styles?: {
    backgroundColor?: string
    textColor?: string
    padding?: string
    margin?: string
  }
  merchantBlockchain?: string // Fallback blockchain for collections
}

export function ListSection({
  props,
  styles,
  merchantBlockchain,
}: ListSectionComponentProps) {
  const {
    title,
    description,
    layout,
    columns,
    items,
    ctaText,
    ctaLink,
    spacing,
  } = props

  return (
    <div
      style={{
        color: styles?.textColor,
        padding: styles?.padding,
        margin: styles?.margin,
      }}
    >
      {/* Header */}
      <div className="mb-6 text-left md:mb-8">
        <h3 className="mb-2 font-bold text-xl md:mb-3 md:text-2xl lg:text-3xl">
          {title}
        </h3>
        {description && (
          <p className="text-sm opacity-70 md:text-base lg:text-lg">
            {description}
          </p>
        )}
      </div>

      {/* Items */}
      {layout === "collectors" && (
        <CollectorsLayout items={items} spacing={spacing} />
      )}

      {layout === "characters" && <CharactersLayout items={items} />}

      {layout === "media" && <MediaLayout items={items} />}

      {layout === "merch" && <MerchLayout items={items} columns={columns} />}

      {layout === "collections" && (
        <CollectionsLayout
          items={items}
          merchantBlockchain={merchantBlockchain}
        />
      )}

      {/* CTA Button */}
      {ctaText && ctaLink && (
        <div className="mt-8 flex justify-center md:mt-12">
          <a
            href={ctaLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center gap-1 rounded-full border-[#F73FF3] border-[3px] bg-transparent px-8 py-4 text-center font-normal text-lg text-white leading-6 transition-all hover:scale-105 active:scale-95"
            style={{
              fontFamily: '"Faro Display Variable Trial", sans-serif',
            }}
          >
            {ctaText}
          </a>
        </div>
      )}

      {/* Empty State */}
      {items.length === 0 && (
        <div className="py-12 text-center opacity-50">
          <p>No items to display</p>
        </div>
      )}
    </div>
  )
}

function CharactersLayout({ items }: { items: ListItem[] }) {
  // Characters layout uses a more compact grid with consistent spacing
  const getCharactersLayout = (): {
    gridCols: string
    gap: string
    imageSize: "small" | "medium" | "default"
  } => {
    const itemCount = items.length

    // For characters, we want an optimized responsive layout
    // Use more columns on mobile to make items smaller and reduce scrolling
    return {
      gridCols:
        "grid-cols-3 xs:grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-6",
      gap: "gap-2 xs:gap-3 sm:gap-3 md:gap-4 lg:gap-6",
      imageSize: "default" as const,
    }
  }

  const layout = getCharactersLayout()

  return (
    <div className={`grid ${layout.gridCols} ${layout.gap}`}>
      {items.map((item) => (
        <CharacterCard key={item.id} item={item} />
      ))}
    </div>
  )
}

function CharacterCard({ item }: { item: ListItem }) {
  const href = item.ctaUrl || item.link

  const content = (
    <div className="group text-center">
      <div className="relative mb-6">
        <div className="aspect-square w-full rounded-lg">
          {item.image ? (
            <img
              src={item.image}
              alt={item.title}
              className="h-full w-full object-cover transition-transform group-hover:scale-110"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-700">
              <span className="font-bold text-gray-400 text-lg md:text-xl">
                {item.title.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>
        {/* Badge for metadata */}
        {item.metadata?.badge != null && (
          <div className="-bottom-1 -right-1 absolute rounded-lg bg-orange-500 p-1">
            <div className="h-3 w-3 bg-white" />
          </div>
        )}
      </div>

      <div>
        <h4 className="mb-0.5 font-medium text-foreground text-xs md:text-sm">
          {item.title}
        </h4>
        {item.description ? (
          <p className="text-[10px] text-foreground/70 md:text-xs">
            {item.description}
          </p>
        ) : item.metadata?.number != null ? (
          <p className="text-[10px] text-gray-400 md:text-xs">
            #{String(item.metadata.number)}
          </p>
        ) : null}
      </div>
    </div>
  )

  return href ? (
    <a href={href} className="block" target="_blank" rel="noopener noreferrer">
      {content}
    </a>
  ) : (
    content
  )
}

function CollectorsLayout({
  items,
  spacing = "default",
}: { items: ListItem[]; spacing?: "default" | "large" }) {
  // Calculate items per row based on screen size and item count
  const getResponsiveLayout = (): {
    gridCols: string
    gap: string
    imageSize: "small" | "medium" | "default"
  } => {
    const itemCount = items.length
    const isLargeSpacing = spacing === "large"

    // For very few items (1-2), use larger spacing and smaller images
    if (itemCount <= 2) {
      return {
        gridCols: "grid-cols-1 xs:grid-cols-1 sm:grid-cols-2",
        gap: isLargeSpacing
          ? "gap-12 xs:gap-14 sm:gap-16 md:gap-20 lg:gap-24"
          : "gap-8 xs:gap-10 sm:gap-8 md:gap-12",
        imageSize: "small" as const,
      }
    }

    // For 3-4 items, moderate spacing
    if (itemCount <= 4) {
      return {
        gridCols:
          "grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
        gap: isLargeSpacing
          ? "gap-10 xs:gap-8 sm:gap-12 md:gap-16 lg:gap-20"
          : "gap-6 xs:gap-4 sm:gap-6 md:gap-8",
        imageSize: "medium" as const,
      }
    }

    // For 5-8 items, use smaller images with more spacing in a 6-column layout
    if (itemCount <= 8) {
      return {
        gridCols:
          "grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6",
        gap: isLargeSpacing
          ? "gap-8 xs:gap-6 sm:gap-8 md:gap-12 lg:gap-16 xl:gap-20"
          : "gap-6 xs:gap-4 sm:gap-5 md:gap-6 lg:gap-8 xl:gap-10",
        imageSize: "small" as const,
      }
    }

    // For many items (9+), use default compact layout
    return {
      gridCols:
        "grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6",
      gap: isLargeSpacing
        ? "gap-6 xs:gap-4 sm:gap-6 md:gap-8 lg:gap-12 xl:gap-16"
        : "gap-4 xs:gap-3 sm:gap-4 md:gap-5 lg:gap-6",
      imageSize: "default" as const,
    }
  }

  const layout = getResponsiveLayout()

  return (
    <div className={`grid ${layout.gridCols} ${layout.gap}`}>
      {items.map((item) => (
        <CollectorCard key={item.id} item={item} imageSize={layout.imageSize} />
      ))}
    </div>
  )
}

function CollectorCard({
  item,
  imageSize = "default",
}: {
  item: ListItem
  imageSize?: "small" | "medium" | "default"
}) {
  const href = item.ctaUrl || item.link

  // Get responsive sizing classes based on imageSize prop
  const getSizeClasses = () => {
    switch (imageSize) {
      case "small":
        return {
          container: "w-full max-w-[200px] mx-auto sm:max-w-[140px]",
          spacing: "mb-4 sm:mb-6",
          title: "text-base sm:text-sm md:text-base",
          description: "text-sm sm:text-xs",
        }
      case "medium":
        return {
          container: "w-full max-w-[240px] mx-auto sm:max-w-[180px]",
          spacing: "mb-3 sm:mb-4",
          title: "text-base sm:text-sm",
          description: "text-sm sm:text-xs",
        }
      default:
        return {
          container: "w-full",
          spacing: "mb-3",
          title: "text-base sm:text-sm",
          description: "text-sm sm:text-xs",
        }
    }
  }

  const sizeClasses = getSizeClasses()

  const content = (
    <div className={`group text-center ${sizeClasses.container}`}>
      <div className={`relative ${sizeClasses.spacing}`}>
        <div className="aspect-square w-full overflow-hidden rounded-full">
          {item.image ? (
            <img
              src={item.image}
              alt={item.title}
              className="h-full w-full object-cover transition-transform group-hover:scale-110"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-700">
              <span className="font-bold text-2xl text-gray-400">
                {item.title.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>
        {/* Badge for metadata */}
        {item.metadata?.badge != null && (
          <div className="-bottom-1 -right-1 absolute rounded-lg bg-orange-500 p-1">
            <div className="h-4 w-4 bg-white" />
          </div>
        )}
      </div>

      <div>
        <h4 className={`mb-1 ${sizeClasses.title} text-foreground`}>
          {item.title}
        </h4>
        {item.description ? (
          <p className={`text-foreground/70 ${sizeClasses.description}`}>
            {item.description}
          </p>
        ) : item.metadata?.number != null ? (
          <p className={`text-gray-400 ${sizeClasses.description}`}>
            #{String(item.metadata.number)}
          </p>
        ) : null}
      </div>
    </div>
  )

  return href ? (
    <a href={href} className="block" target="_blank" rel="noopener noreferrer">
      {content}
    </a>
  ) : (
    content
  )
}

function MediaLayout({ items }: { items: ListItem[] }) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <MediaCard key={item.id} item={item} />
      ))}
    </div>
  )
}

function MediaCard({ item }: { item: ListItem }) {
  const href = item.ctaUrl || item.link

  const content = (
    <div className="group cursor-pointer">
      {/* Image */}
      <div className="aspect-[16/9] w-full overflow-hidden rounded-xl">
        {item.image ? (
          <img
            src={item.image}
            alt={item.title}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-700">
            <span className="text-gray-400">No Image</span>
          </div>
        )}
      </div>

      {/* Content Below Image */}
      <div className="mt-4">
        <h4 className="mb-2 text-foreground text-lg leading-tight">
          {item.title}
        </h4>
        {item.description && (
          <p className="text-foreground/70 text-sm leading-relaxed">
            {item.description}
          </p>
        )}
        {item.metadata?.date != null && (
          <p className="mt-2 text-foreground/50 text-xs">
            {String(item.metadata.date)}
          </p>
        )}
      </div>
    </div>
  )

  return href ? (
    <a href={href} className="block" target="_blank" rel="noopener noreferrer">
      {content}
    </a>
  ) : (
    <div className="cursor-default">{content}</div>
  )
}

function MerchLayout({
  items,
  columns,
}: { items: ListItem[]; columns?: 2 | 3 | 4 | 6 }) {
  const getGridColumns = () => {
    switch (columns) {
      case 2:
        return "grid-cols-1 sm:grid-cols-2"
      case 3:
        return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      case 4:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
      case 6:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
      default:
        return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
    }
  }

  return (
    <div className={`grid ${getGridColumns()}`} style={{ gap: "45px" }}>
      {items.map((item) => (
        <MerchCard key={item.id} item={item} />
      ))}
    </div>
  )
}

function MerchCard({ item }: { item: ListItem }) {
  const href = item.ctaUrl || item.link
  const ctaText = item.ctaText // Only use explicit CTA text

  return (
    <div className="group flex h-full flex-col">
      <div className="mb-4 aspect-[4/3] w-full overflow-hidden rounded-xl bg-white">
        {item.image ? (
          <img
            src={item.image}
            alt={item.title}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <span className="text-gray-400">No Image</span>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col text-left">
        <h4 className="mb-3 text-foreground text-lg leading-tight">
          {item.title}
        </h4>

        {/* Only show button if there's CTA text */}
        {ctaText && (
          <div className="mt-auto">
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-2xl bg-white/10 px-6 py-3 font-medium text-sm text-white transition-all hover:bg-white/20 active:scale-95"
              >
                {ctaText}
              </a>
            ) : (
              <button
                type="button"
                className="inline-flex items-center rounded-2xl bg-white/10 px-6 py-3 font-medium text-sm text-white transition-all hover:bg-white/20 active:scale-95"
              >
                {ctaText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function CollectionsLayout({
  items,
  merchantBlockchain,
}: { items: ListItem[]; merchantBlockchain?: string }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((item) => (
        <CollectionCard
          key={item.id}
          item={item}
          merchantBlockchain={merchantBlockchain}
        />
      ))}
    </div>
  )
}

function CollectionCardWrapper({ children }: { children: React.ReactNode }) {
  return <div className={cn("aspect-square w-full")}>{children}</div>
}

function ProgressiveBlurMask() {
  return (
    <>
      <div
        className="pointer-events-none absolute inset-0 z-20"
        style={{
          backdropFilter: "blur(6.0px)",
          maskImage:
            "linear-gradient(to top, black 0%, black 11%, transparent 35%)",
          WebkitMaskImage:
            "linear-gradient(to top, black 0%, black 11%, transparent 35%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 z-20"
        style={{
          backdropFilter: "blur(10.0px)",
          maskImage: "linear-gradient(to top, black 0%, transparent 25%)",
          WebkitMaskImage: "linear-gradient(to top, black 0%, transparent 25%)",
        }}
      />

      <div
        className="pointer-events-none absolute inset-0 z-20"
        style={{
          backdropFilter: "blur(6.0px)",
          maskImage:
            "linear-gradient(to bottom, black 0%, black 11%, transparent 35%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, black 0%, black 11%, transparent 35%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 z-20"
        style={{
          backdropFilter: "blur(10.0px)",
          maskImage: "linear-gradient(to bottom, black 0%, transparent 25%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, black 0%, transparent 25%)",
        }}
      />
    </>
  )
}

function CollectionCard({
  item,
  merchantBlockchain,
}: { item: ListItem; merchantBlockchain?: string }) {
  // Only fetch stats if we have a valid collectionId (always call hook for React rules)
  const hasValidCollectionId = Boolean(item.collectionId?.trim())

  // Construct proper CollectionId with blockchain prefix
  const getCollectionId = (): CollectionId => {
    if (!item.collectionId) return "" as CollectionId

    // If already has blockchain prefix (contains ":"), use as-is
    if (item.collectionId.includes(":")) {
      return item.collectionId as CollectionId
    }

    // Otherwise, add blockchain prefix
    const blockchain =
      item.blockchain?.toUpperCase() ||
      merchantBlockchain?.toUpperCase() ||
      "ETHEREUM"
    return joinAddress(blockchain, item.collectionId) as CollectionId
  }

  const collectionId = getCollectionId()
  const {
    data: collectionStats,
    error,
    isLoading,
  } = useCollectionStatitstic(collectionId)

  // Build URL for blockchain/collections/collectionId
  const getCollectionUrl = () => {
    if (item.collectionId) {
      const blockchain =
        item.blockchain?.toLowerCase() ||
        merchantBlockchain?.toLowerCase() ||
        "ethereum" // fallback to merchant blockchain, then ethereum
      return `/${blockchain}/collections/${item.collectionId}`
    }
    return item.ctaUrl || item.link || "#"
  }

  const href = getCollectionUrl()

  return (
    <CollectionCardWrapper>
      <a
        href={href}
        className={cn(
          "group flex-shrink-0 rounded-lg border border-white/10 p-4 md:rounded-xl",
          "relative flex h-full w-full flex-col justify-between overflow-hidden",
        )}
      >
        <div className="absolute inset-0">
          <Image
            containerClassName="transition-transform group-hover:scale-105 opacity-50"
            className="object-cover"
            imageSrc={item.image}
            alt={item.title}
          />
        </div>
        <ProgressiveBlurMask />

        <div className="relative z-30 flex h-full flex-col justify-between">
          <h6 className="w-full truncate font-headline text-l md:text-lg">
            {item.title}
          </h6>
          <div className="flex flex-col gap-[2px]">
            {hasValidCollectionId && collectionStats?.floor && (
              <PriceInUsd
                className="text-base md:text-lg"
                amount={collectionStats.floor.amount}
                currency={collectionStats.floor.currency}
              />
            )}
            {hasValidCollectionId && collectionStats?.dayFloorChange && (
              <ChangeTrend
                changePercent={collectionStats.dayFloorChange}
                className="text-xs md:text-sm"
              />
            )}
          </div>
        </div>
      </a>
    </CollectionCardWrapper>
  )
}
